import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle } from "lucide-react";
import api from "@/lib/api";

function formatError(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true); setError("");
    try {
      await api.post("/client-auth/reset-password", { token, password });
      setDone(true);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#1C1917] flex items-center justify-center px-5 py-16">
      <div data-testid="reset-page" className="max-w-md w-full bg-white border border-[#1C1917]/10 rounded-[1.5rem] p-8">
        <div className="eyebrow text-[#C5B382] mb-2">My Account</div>
        <h1 className="font-serif text-3xl mb-6">Reset password</h1>

        {!token ? (
          <p className="text-sm text-[#1C1917]/60">This reset link is invalid. Please request a new one from the login screen.</p>
        ) : done ? (
          <div data-testid="reset-done" className="text-center py-4">
            <CheckCircle size={36} className="text-[#C5B382] mx-auto" />
            <p className="mt-3 text-sm text-[#1C1917]/70">Your password has been updated. You can now sign in.</p>
            <Link to="/" className="inline-block mt-6 bg-[#C5B382] text-[#1C1917] text-[11px] font-semibold tracking-[0.2em] uppercase px-8 py-3.5 rounded-full">Back to Home</Link>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="eyebrow text-[#1C1917]/45 block mb-2">New password</label>
              <input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#FAF8F5] border border-[#1C1917]/15 rounded-xl focus:border-[#C5B382] outline-none px-4 py-3 text-sm" />
            </div>
            <div>
              <label className="eyebrow text-[#1C1917]/45 block mb-2">Confirm password</label>
              <input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                className="w-full bg-[#FAF8F5] border border-[#1C1917]/15 rounded-xl focus:border-[#C5B382] outline-none px-4 py-3 text-sm" />
            </div>
            {error && <p data-testid="reset-error" className="text-red-500 text-sm">{error}</p>}
            <button data-testid="reset-submit" disabled={busy || !password} onClick={submit}
              className="w-full flex items-center justify-center gap-2 bg-[#C5B382] hover:bg-[#B8A572] disabled:opacity-50 text-[#1C1917] text-[12px] font-semibold tracking-[0.15em] uppercase py-3.5 rounded-full transition-colors">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null} Update password
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
